// src/components/Tools/Results/Wafw00fResults.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, ShieldX } from 'lucide-react';
import type { ResultRendererProps } from './types';

type Row = {
  detected?: boolean;
  firewall?: string;
  manufacturer?: string;
  trigger_url?: boolean;
  url?: string;
};

function normalize(results: any[]): Row[] {
  if (!Array.isArray(results)) return [];
  return results.map((r) => ({
    detected: !!r?.detected,
    firewall: String(r?.firewall ?? ''),
    manufacturer: String(r?.manufacturer ?? ''),
    trigger_url: !!r?.trigger_url,
    url: String(r?.url ?? ''),
  }));
}

const Wafw00fResults: React.FC<ResultRendererProps> = ({ results }) => {
  const { t } = useTranslation();
  const rows = React.useMemo(() => normalize(results), [results]);

  if (rows.length === 0) {
    return (
      <div className="w-full flex justify-center">
        <p className="text-sm text-slate-500">{t('tools.results_scan.wafw00f.empty')}</p>
      </div>
    );
  }

  return (
    <div className="w-full flex justify-center">
      <div className="w-full max-w-3xl space-y-6">
        {/* Header centrado */}
        <div className="text-center space-y-1">
          <h3 className="text-xl font-bold text-slate-900">
            {t('tools.results_scan.wafw00f.title')}
          </h3>
          <p className="text-sm text-slate-500">
            {t('tools.results_scan.wafw00f.found_count', { count: rows.length })}
          </p>
        </div>

        {/* Tarjetas */}
        <div className="grid grid-cols-1 gap-4">
          {rows.map((r, i) => {
            const detected = !!r.detected;
            const statusKey = detected ? 'detected' : 'not_detected';
            const statusClasses = detected
              ? 'bg-emerald-100 text-emerald-800 ring-emerald-200'
              : 'bg-rose-100 text-rose-800 ring-rose-200';
            const StatusIcon = detected ? ShieldCheck : ShieldX;

            return (
              <div key={i} className="rounded-2xl border border-slate-200 overflow-hidden bg-white">
                {/* Cabecera tarjeta */}
                <div className="px-4 py-3 bg-gradient-to-r from-slate-800 to-slate-900 text-white flex items-center justify-between gap-3">
                  <div className="font-semibold text-sm truncate">
                    {r.url || t('tools.results_scan.wafw00f.unknown_url')}
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-1 ring-1 ${statusClasses}`}
                  >
                    <StatusIcon className="h-3.5 w-3.5" />
                    {t(`tools.results_scan.wafw00f.${statusKey}`)}
                  </span>
                </div>

                {/* Cuerpo */}
                <div className="p-4 text-sm">
                  <dl className="grid grid-cols-[140px_1fr] gap-y-2">
                    <dt className="font-medium text-slate-700">
                      {t('tools.results_scan.wafw00f.firewall')}
                    </dt>
                    <dd className="text-slate-900 break-words">{r.firewall || '—'}</dd>

                    <dt className="font-medium text-slate-700">
                      {t('tools.results_scan.wafw00f.manufacturer')}
                    </dt>
                    <dd className="text-slate-900 break-words">{r.manufacturer || '—'}</dd>

                    <dt className="font-medium text-slate-700">
                      {t('tools.results_scan.wafw00f.trigger_url')}
                    </dt>
                    <dd className="text-slate-900">
                      {r.trigger_url ? t('tools.results_scan.wafw00f.yes') : t('tools.results_scan.wafw00f.no')}
                    </dd>
                  </dl>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Wafw00fResults;
